import React from "react";

import { config } from "@app/config";
import { formatCentsAsMoney } from "@app/lib/utils";

type Locales = {
  [key: string]: string;
};

type Shop = {
  name: string;
};

export type Props = {
  config?: typeof config;
  locales?: Locales;
  shop?: Shop;
  values?: {
    price?: number;
    compareAtPrice?: number;
    membersPrice?: number;
  };
};

function Money({ value, className }: { value?: number; className?: string }) {
  if (!value) return null;
  const money = formatCentsAsMoney(value);

  return <p className={className}>{money}</p>;
}

export function MemberPricing({ values, locales }: Props) {
  if (!values) return null;
  const { price, membersPrice, compareAtPrice } = values;

  const isLoggedIn = window.SHOPIFY_DATA.customer.loggedIn;
  const isSale = compareAtPrice && price && price < compareAtPrice;

  if (!isSale) {
    return (
      <div className="group flex gap-2 md:gap-5" data-ui={`${isLoggedIn ? "member" : ""}`}>
        <div>
          <p className="text-label text-neutrals-grey-2">{locales?.regular}:</p>
          <Money
            value={price}
            className="text-neutrals-black group-ui-[member]:text-neutrals-grey-2 group-ui-[member]:line-through"
          />
        </div>
        <div>
          <p className="text-label text-neutrals-grey-2">{locales?.member}:</p>
          <Money value={membersPrice} className="text-red-base" />
        </div>
      </div>
    );
  }

  return (
    <div className="group flex gap-2 md:gap-5" data-ui={`${isLoggedIn ? "member" : ""}`}>
      <div>
        <p className="text-label text-neutrals-grey-2">{locales?.was}:</p>
        <Money value={compareAtPrice} className="text-neutrals-grey-2 group-ui-[member]:line-through" />
      </div>
      <div className="group-ui-[member]:hidden">
        <p className="text-label text-neutrals-grey-2">{locales?.now}:</p>
        <Money value={price} className="text-neutrals-grey-2 group-ui-[member]:line-through" />
      </div>
      <div>
        <p className="text-label text-neutrals-grey-2">{locales?.member}:</p>
        <Money value={membersPrice} className="text-red-base" />
      </div>
    </div>
  );
}

export function RegularPricing({ values, locales }: Props) {
  if (!values) return null;
  const { price, compareAtPrice } = values;

  const isSale = compareAtPrice && price && price < compareAtPrice;

  if (!isSale) return <Money value={price} className="text-body-md" />;

  return (
    <div className="flex gap-2 md:gap-5">
      <div>
        <p className="text-label text-neutrals-grey-2">{locales?.was}:</p>
        <Money value={compareAtPrice} className="text-neutrals-grey-2 line-through" />
      </div>
      <div>
        <p className="text-label text-neutrals-grey-2">{locales?.now}:</p>
        <Money value={price} className="text-red-base" />
      </div>
    </div>
  );
}

export function Price({ values, locales }: Props) {
  if (!values) return null;
  const isMember = values.membersPrice;

  if (isMember) return <MemberPricing values={values} locales={locales} />;
  return <RegularPricing values={values} locales={locales} />;
}
